import { useState } from 'react';
import { Modal, Button, Select, Alert } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconGitMerge, IconAlertTriangle, IconTrash, IconCheck } from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import { useBusinesses, useMergeBusiness } from '@/hooks/api-hooks';

interface Props {
    opened: boolean;
    onClose: () => void;
    targetBusiness: Business;
}

export default function MergeBusinessModal({ opened, onClose, targetBusiness }: Props) {
    const { t } = useTranslation();
    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

    const { data: businessesRes, isLoading: isLoadingBusinesses } = useBusinesses();
    const businesses = businessesRes?.data || [];

    const mergeMutation = useMergeBusiness();

    const availableBusinesses = businesses.filter(b => Number(b.id) !== Number(targetBusiness.id));
    const selectData = availableBusinesses.map(b => ({
        value: String(b.id),
        label: `${b.name} (${b.category ? t(b.category) : t('business')} #${b.id})`
    }));

    const selectedSourceBusiness = availableBusinesses.find(b => String(b.id) === selectedSourceId);

    const handleMerge = () => {
        if (!selectedSourceId) return;

        mergeMutation.mutate(
            {
                targetBusinessId: targetBusiness.id,
                sourceBusinessId: Number(selectedSourceId),
            },
            {
                onSuccess: () => {
                    setSelectedSourceId(null);
                    onClose();
                }
            }
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2 font-bold text-slate-800">
                    <IconGitMerge size={20} className="text-red-600" />
                    <span>{t('merge_with_another_business')}</span>
                </div>
            }
            size="lg"
            centered
        >
            <div className="space-y-4 text-xs">
                <p className="text-slate-600 leading-relaxed">
                    {t('merge_business_description')}
                </p>

                <Select
                    label={t('select_source_business_to_delete')}
                    placeholder={t('select_source_business_to_delete')}
                    data={selectData}
                    value={selectedSourceId}
                    onChange={(val) => setSelectedSourceId(val)}
                    searchable
                    disabled={isLoadingBusinesses || mergeMutation.isPending}
                    nothingFoundMessage={t('no_results')}
                    className="font-semibold"
                    size="sm"
                />

                {selectedSourceBusiness && (
                    <div className="space-y-3 pt-1">
                        {/* Side-by-side comparison of source vs recipient */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* SOURCE BUSINESS: TO BE DELETED */}
                            <div className="p-3 bg-red-50/90 border-2 border-red-300 rounded-xl relative flex flex-col justify-between shadow-2xs">
                                <div className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md tracking-wider self-start mb-2">
                                    <IconTrash size={12} stroke={2.5} />
                                    {t('business_to_be_deleted')}
                                </div>
                                <div>
                                    <div className="font-bold text-red-950 text-sm truncate">
                                        {selectedSourceBusiness.name}
                                    </div>
                                    <div className="text-xs text-red-800 font-semibold mt-0.5">
                                        #{selectedSourceBusiness.id} · {selectedSourceBusiness.category ? t(selectedSourceBusiness.category) : t('business')}
                                    </div>
                                </div>
                            </div>

                            {/* DESTINATION BUSINESS: RECIPIENT / KEPT */}
                            <div className="p-3 bg-emerald-50/90 border-2 border-emerald-300 rounded-xl relative flex flex-col justify-between shadow-2xs">
                                <div className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md tracking-wider self-start mb-2">
                                    <IconCheck size={12} stroke={2.5} />
                                    {t('business_to_be_kept')}
                                </div>
                                <div>
                                    <div className="font-bold text-emerald-950 text-sm truncate">
                                        {targetBusiness.name}
                                    </div>
                                    <div className="text-xs text-emerald-800 font-semibold mt-0.5">
                                        #{targetBusiness.id} · {targetBusiness.category ? t(targetBusiness.category) : t('business')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CRITICAL WARNING ALERT */}
                        <Alert
                            icon={<IconAlertTriangle size={20} className="text-red-700 shrink-0" />}
                            color="red"
                            variant="filled"
                            title={t('confirm_merge_business')}
                            className="rounded-xl shadow-xs"
                        >
                            <div className="text-xs font-semibold leading-relaxed">
                                {t('merge_and_delete_warning')}
                            </div>
                        </Alert>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={onClose}
                        disabled={mergeMutation.isPending}
                        size="sm"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        onClick={handleMerge}
                        loading={mergeMutation.isPending}
                        disabled={!selectedSourceId}
                        size="sm"
                    >
                        {selectedSourceBusiness
                            ? `${t('merge_and_delete_action')} (#${selectedSourceBusiness.id})`
                            : t('merge_and_delete_action')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
